import { z } from 'zod';
import type { ParseResult } from '../voyage/schemas.js';
import { portCodeField } from './catalog.js';

/**
 * Seferin güzergâhı (ADR 0048 §3): `from` + ara limanlar (`voyage_ports`, sırayla) + `to`. Uçlar
 * seferin kendi kolonlarındadır (tek doğruluk kaynağı). Liman kodu yoksa serbest metin kalır.
 */

/** En çok ara liman. */
export const MAX_VOYAGE_PORTS = 20;

export type ItineraryRole = 'from' | 'via' | 'to';

export interface ItineraryStop {
  /** `from`, `to` ya da ara limanın kimliği (M13f liman aramasını buna bağlar). */
  key: string;
  role: ItineraryRole;
  portCode: string | null;
  portName: string;
}

export interface ItineraryVoyage {
  fromPort: string;
  fromPortCode?: string | null;
  toPort: string;
  toPortCode?: string | null;
}

export interface ItineraryVia {
  id: string;
  seq: number;
  portCode: string | null;
  portName: string;
}

export function voyageItinerary(
  voyage: ItineraryVoyage,
  vias: readonly ItineraryVia[],
): ItineraryStop[] {
  return [
    { key: 'from', role: 'from', portCode: voyage.fromPortCode ?? null, portName: voyage.fromPort },
    ...[...vias]
      .sort((a, b) => a.seq - b.seq)
      .map((via) => ({
        key: via.id,
        role: 'via' as const,
        portCode: via.portCode,
        portName: via.portName,
      })),
    { key: 'to', role: 'to', portCode: voyage.toPortCode ?? null, portName: voyage.toPort },
  ];
}

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});

const voyagePortSchema = z.object({
  portCode: portCodeField(),
  portName: z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().min(1, 'REQUIRED').max(200, 'TOO_LONG')),
});

export const voyagePortsSchema = z
  .array(voyagePortSchema, typed('INVALID_LIST'))
  .max(MAX_VOYAGE_PORTS, 'TOO_MANY_PORTS');
export type VoyagePortInput = z.output<typeof voyagePortSchema>;

export function parseVoyagePorts(input: unknown): ParseResult<VoyagePortInput[]> {
  const result = voyagePortsSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        issues: result.error.issues.map((issue) => ({
          path: issue.path.map(String).join('.'),
          code: issue.message,
        })),
      };
}
